/**
 * The access token lives here, in memory only. It is never written to localStorage or sessionStorage, so
 * a script that runs on the page cannot read it from storage.
 *
 * The refresh token is an httpOnly cookie that scripts cannot see at all. Reloading the page loses the
 * memory copy; restoreSession() gets a new access token from the cookie before the app renders.
 */
import { API_BASE_URL } from '../config/api';

type Listener = (token: string | null) => void;

let accessToken: string | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let inflight: Promise<string> | null = null;
const listeners = new Set<Listener>();

/** Refresh this long before the token expires. */
const REFRESH_AHEAD_MS = 60_000;

export const getAccessToken = (): string | null => accessToken;

export function onTokenChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Milliseconds since epoch at which the JWT expires, or null if it cannot be read. */
export function tokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const exp = JSON.parse(atob(payload)).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function schedule(token: string | null): void {
  if (timer) clearTimeout(timer);
  timer = undefined;
  if (!token) return;
  const expiry = tokenExpiry(token);
  if (!expiry) return;
  const delay = Math.max(5_000, expiry - Date.now() - REFRESH_AHEAD_MS);
  timer = setTimeout(() => {
    refreshAccessToken().catch(() => undefined);
  }, delay);
}

/**
 * A non-secret hint that this browser has signed in, so startup only asks for a session when one may
 * exist (an anonymous visitor would otherwise cause a 401 on every page load). It holds no credential.
 */
const SESSION_HINT = 'flahafast.session';
const hint = {
  set: () => { try { localStorage.setItem(SESSION_HINT, '1'); } catch { /* storage unavailable */ } },
  clear: () => { try { localStorage.removeItem(SESSION_HINT); } catch { /* storage unavailable */ } },
  has: () => { try { return localStorage.getItem(SESSION_HINT) === '1'; } catch { return false; } },
};

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) hint.set(); else hint.clear();
  schedule(token);
  listeners.forEach((l) => l(token));
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

/**
 * Ask the server for a new access token using the refresh cookie. Concurrent callers share one request:
 * the server rotates the refresh token on every use, so two parallel requests would make the second one
 * look like a stolen, replayed token.
 */
export function refreshAccessToken(): Promise<string> {
  if (!inflight) {
    inflight = fetch(`${API_BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) throw new Error('Session expired');
        const body = await response.json();
        const token = body?.data?.token;
        if (typeof token !== 'string') throw new Error('Session expired');
        setAccessToken(token);
        return token;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Called once at startup. Resolves to the restored token, or null when there is no valid session. */
export async function restoreSession(): Promise<string | null> {
  // Older builds kept tokens in localStorage. Remove them so no stale copy stays readable.
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  } catch {
    /* storage unavailable */
  }
  if (!hint.has()) return null;
  try {
    return await refreshAccessToken();
  } catch {
    hint.clear();
    return null;
  }
}

/** A background tab's timers are throttled. Top the token up when the user comes back. */
export function installVisibilityRefresh(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !accessToken) return;
    const expiry = tokenExpiry(accessToken);
    if (expiry && expiry - Date.now() < 2 * REFRESH_AHEAD_MS) refreshAccessToken().catch(() => undefined);
  });
}
